import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSiteContent } from "@/lib/siteContent";
import { resolveImageUrl } from "@/lib/api";
import QuickBookingDrawer from "@/components/QuickBookingDrawer";
import HotelLayout from "@/components/HotelLayout";
import {
    LayoutDashboard, Building2, MapPin, ClipboardList, Users, BarChart3, Wallet,
    Settings, LogOut, Menu, X, ExternalLink, Sun, Moon, FileText, MessageSquare, Plus, TrendingUp, Megaphone, Tag, Image as ImageIcon, Mail, Activity, Flag, PanelLeftClose, PanelLeftOpen, LineChart, Headset
} from "lucide-react";
import api from "@/lib/api";

function SupportUnreadBadge({ collapsed, url = "/support/unread", pick = (d) => d.unread_tickets, testid = "nav-support-unread" }) {
    const [n, setN] = useState(0);
    useEffect(() => {
        const tick = () => { if (!document.hidden) api.get(url).then((r) => setN(pick(r.data) || 0)).catch(() => {}); };
        tick();
        const t = setInterval(tick, 15000);
        window.addEventListener("admin-badges-refresh", tick);
        return () => { clearInterval(t); window.removeEventListener("admin-badges-refresh", tick); };
    }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!n) return null;
    return <span data-testid={testid} className={`${collapsed ? "absolute top-1 right-1 h-4 min-w-4 text-[9px]" : "ml-auto h-5 min-w-5 text-[10px]"} px-1 rounded-full bg-rose-500 text-white font-bold flex items-center justify-center`}>{n}</span>;
}

const HOTEL_ROLES = new Set(["otel_owner", "otel_manager", "otel_reception", "otel_accounting"]);

// Role-based menu visibility
const ROLE_MENUS = {
    super_admin: ["dashboard", "hotels", "applications", "partners", "destinations", "bookings", "support", "finance", "refunds", "users", "reports", "analytics", "reviews", "settings", "site_content", "cms_pages", "ads", "coupons", "redirects", "media", "newsletter", "feature_flags", "audit_log"],
    admin: ["dashboard", "hotels", "applications", "partners", "destinations", "bookings", "support", "finance", "refunds", "users", "reports", "analytics", "reviews", "settings", "site_content", "cms_pages", "ads", "coupons", "redirects", "media", "newsletter", "feature_flags", "audit_log"],
    operations: ["dashboard", "bookings", "support", "reviews", "analytics"],
    finance: ["dashboard", "finance", "refunds", "bookings"],
    finance_approver: ["dashboard", "finance", "refunds", "bookings", "reports"],
    content: ["dashboard", "hotels", "destinations", "reviews", "site_content", "analytics"],
};

const NAV = [
    { key: "dashboard", to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
    { key: "hotels", to: "/admin/hotels", icon: Building2, label: "Oteller", testid: "nav-hotels" },
    { key: "destinations", to: "/admin/destinations", icon: MapPin, label: "Destinasyonlar", testid: "nav-destinations" },
    { key: "applications", to: "/admin/hotel-applications", icon: ClipboardList, label: "Tesis Başvuruları", testid: "nav-applications" },
    { key: "bookings", to: "/admin/bookings", icon: ClipboardList, label: "Rezervasyonlar", testid: "nav-bookings" },
    { key: "support", to: "/admin/support", icon: Headset, label: "Destek Talepleri", testid: "nav-support" },
    { key: "finance", to: "/admin/finance", icon: Wallet, label: "Finans", testid: "nav-finance" },
    { key: "refunds", to: "/admin/refunds", icon: Wallet, label: "İade Bekleyenler", testid: "nav-refunds" },
    { key: "users", to: "/admin/users", icon: Users, label: "Kullanıcılar", testid: "nav-users" },
    { key: "reports", to: "/admin/reports", icon: BarChart3, label: "Raporlar", testid: "nav-reports" },
    { key: "analytics", to: "/admin/analytics", icon: LineChart, label: "Site Trafiği", testid: "nav-analytics" },
    { key: "reviews", to: "/admin/reviews", icon: MessageSquare, label: "Yorumlar", testid: "nav-reviews" },
    { key: "site_content", to: "/admin/site-content", icon: FileText, label: "Site İçeriği", testid: "nav-site-content" },
    { key: "cms_pages", to: "/admin/cms-pages", icon: FileText, label: "CMS Sayfaları", testid: "nav-cms-pages" },
    { key: "ads", to: "/admin/ads", icon: Megaphone, label: "Reklamlar", testid: "nav-ads" },
    { key: "coupons", to: "/admin/coupons", icon: Tag, label: "Kuponlar", testid: "nav-coupons" },
    { key: "redirects", to: "/admin/redirects", icon: ExternalLink, label: "Yönlendirmeler", testid: "nav-redirects" },
    { key: "media", to: "/admin/media", icon: ImageIcon, label: "Medya Kütüphanesi", testid: "nav-media" },
    { key: "newsletter", to: "/admin/newsletter", icon: Mail, label: "Newsletter", testid: "nav-newsletter" },
    { key: "feature_flags", to: "/admin/feature-flags", icon: Flag, label: "Özellik Bayrakları", testid: "nav-flags" },
    { key: "audit_log", to: "/admin/audit-log", icon: Activity, label: "Denetim Kaydı", testid: "nav-audit" },
    { key: "settings", to: "/admin/settings", icon: Settings, label: "Ayarlar", testid: "nav-settings" },
];

export default function AdminLayout({ children, title, action }) {
    const { user, logout } = useAuth() || {};
    const site = useSiteContent();
    const brand = site?.brand || {};
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
    const [qbOpen, setQbOpen] = useState(false);
    // Sidebar collapse — persisted in localStorage
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem("admin_sidebar_collapsed") === "1"; } catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem("admin_sidebar_collapsed", collapsed ? "1" : "0"); } catch {}
    }, [collapsed]);

    const toggleDark = () => {
        const next = !dark;
        setDark(next);
        document.documentElement.classList.toggle("dark", next);
    };

    // If a hotel-role user lands on an admin-styled page (because they followed
    // a sidebar link that points to /admin/hotels/{id}/...), wrap with the
    // HotelLayout so they always see their own sidebar — never the admin one.
    if (user && HOTEL_ROLES.has(user.role)) {
        return <HotelLayout title={title} action={action}>{children}</HotelLayout>;
    }

    return (
        <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex">
            {/* Sidebar */}
            <aside data-testid="admin-sidebar" className={`fixed lg:static inset-y-0 left-0 z-40 ${collapsed ? "w-16" : "w-64"} bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transform transition-all duration-200 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
                <div className="h-16 flex items-center px-3 border-b border-slate-200 dark:border-slate-800 gap-2">
                    <Link to="/" data-testid="admin-logo" className="flex items-center gap-2 min-w-0 flex-1">
                        {brand.logo_url ? (
                            <img src={resolveImageUrl(brand.logo_url)} alt={brand.name || "Logo"} className={`${collapsed ? "h-8" : "h-10"} max-h-10 w-auto object-contain`} />
                        ) : (
                            <>
                                <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-sky-500 to-amber-400 flex items-center justify-center font-bold text-white flex-shrink-0">{(brand.name || "m")[0].toLowerCase()}</div>
                                {!collapsed && <span className="font-display text-lg font-bold text-slate-900 dark:text-white truncate">{brand.name || "Msonline Travel"}</span>}
                            </>
                        )}
                    </Link>
                    {/* Desktop collapse toggle */}
                    <button
                        onClick={() => setCollapsed((c) => !c)}
                        data-testid="admin-sidebar-collapse"
                        className="hidden lg:inline-flex p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 flex-shrink-0"
                        title={collapsed ? "Menüyü genişlet" : "Menüyü daralt"}
                    >
                        {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                    </button>
                    <button onClick={() => setOpen(false)} className="lg:hidden p-2">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <nav className={`${collapsed ? "px-2" : "px-3"} py-4 space-y-1 overflow-y-auto`} style={{maxHeight: "calc(100vh - 4rem)"}}>
                    {NAV.filter((n) => {
                        const role = user?.role || "admin";
                        const allowed = ROLE_MENUS[role] || ROLE_MENUS.admin;
                        return allowed.includes(n.key);
                    }).map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={n.testid}
                            onClick={() => setOpen(false)}
                            title={collapsed ? n.label : undefined}
                            className={({ isActive }) => `relative flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive
                                ? "bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300"
                                : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                        >
                            <n.icon className="h-4 w-4 flex-shrink-0" /> {!collapsed && <span className="truncate">{n.label}</span>}
                            {n.key === "support" && <SupportUnreadBadge collapsed={collapsed} />}
                            {n.key === "applications" && <SupportUnreadBadge collapsed={collapsed} url="/admin/hotel-applications/pending-count" pick={(d) => d.count} testid="nav-applications-pending" />}
                        </NavLink>
                    ))}
                    <div className="pt-3 mt-3 border-t border-slate-100 dark:border-slate-800 space-y-1">
                        <a href="/" target="_blank" rel="noreferrer" title={collapsed ? "Siteyi Görüntüle" : undefined} className={`flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800`}>
                            <ExternalLink className="h-4 w-4 flex-shrink-0" /> {!collapsed && "Siteyi Görüntüle"}
                        </a>
                        <button onClick={() => { logout?.(); navigate("/"); }} title={collapsed ? "Çıkış" : undefined} className={`w-full flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30`}>
                            <LogOut className="h-4 w-4 flex-shrink-0" /> {!collapsed && "Çıkış"}
                        </button>
                    </div>
                </nav>
            </aside>

            {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

            {/* Main */}
            <div className="flex-1 lg:ml-0 min-w-0">
                <header className="sticky top-0 z-20 min-h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center flex-wrap gap-y-2 gap-x-3 px-4 lg:px-8 py-2.5">
                    <button onClick={() => setOpen(true)} className="lg:hidden p-2 -ml-2 flex-shrink-0"><Menu className="h-5 w-5" /></button>
                    <h1 className="font-display text-lg lg:text-xl font-bold text-slate-900 dark:text-white ml-2 lg:ml-0 truncate min-w-0 max-w-[40%] flex-shrink" title={title}>{title}</h1>
                    <div className="ml-auto flex items-center flex-wrap gap-2 min-w-0">
                        {action}
                        <button
                            type="button"
                            onClick={() => setQbOpen(true)}
                            data-testid="quick-booking-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm transition-colors whitespace-nowrap"
                            title="Panel içinden hızlıca rezervasyon oluştur"
                        >
                            <Plus className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Hızlı Rezervasyon</span>
                        </button>
                        <button onClick={toggleDark} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0">
                            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        </button>
                        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer transition-colors whitespace-nowrap" data-testid="admin-profile-pill" onClick={() => navigate("/admin/profile")} title="Profilim · Şifre Değiştir">
                            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-sky-500 to-amber-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{user?.full_name?.[0]}</div>
                            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[140px]">{user?.full_name}</span>
                        </div>
                    </div>
                </header>
                <main className="p-4 lg:p-8">{children}</main>
            </div>
            <QuickBookingDrawer open={qbOpen} onClose={() => setQbOpen(false)} />
        </div>
    );
}
